<template>
  <v-dialog v-model="dialog" max-width="800" scrollable>
    <v-card>
      <v-card-title>
        <div class="d-flex justify-space-between align-start flex-grow-1">
          <div class="lh-1">
            <h6 class="mb-2">
              Fatura {{ invoice?.month }}/{{ invoice?.year }} -
              {{ invoice?.Company?.name }}
            </h6>
            {{ invoice?.amount | currency }}
          </div>
          <v-chip
            label
            :color="INVOICE_DEFINITIONS.status[invoice?.status]?.color"
            dark
            small
          >
            {{ INVOICE_DEFINITIONS.status[invoice?.status]?.label }}
          </v-chip>
        </div>
      </v-card-title>
      <v-card-text>
        <div class="d-flex justify-end">
          <v-switch
            v-model="showFreeItems"
            label="Mostrar itens sem custo"
            dense
            hide-details
            class="ma-0 pa-0"
          />
        </div>
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
              <tr>
                <th>Item</th>
                <th>Uso</th>
                <th>Custo</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="group in itemsGrouped">
                <tr :key="group.label" class="text-caption font-weight-bold">
                  <td colspan="4">
                    <v-icon small left>{{ group.icon }}</v-icon>
                    {{ group.label }}
                  </td>
                </tr>
                <tr v-for="item in group.items" :key="item.id">
                  <td>{{ INVOICE_DEFINITIONS.itemKey[item.itemKey].label }}</td>
                  <td>{{ formatUsage(item.usage, item.itemKey) }}</td>
                  <td v-if="item.priceRuleId">
                    {{
                      INVOICE_DEFINITIONS.pricingTypes[
                        item.PriceRule.pricingType
                      ].formatter(item.PriceRule.config, item.type)
                    }}
                  </td>
                  <td v-else>
                    <i class="text-caption">Sem custo</i>
                  </td>
                  <td>{{ item.totalAmount | currency }}</td>
                </tr>
                <tr :key="group.label + 'subtotal'" class="text--secondary">
                  <td colspan="3" class="text-right">
                    <span class="font-weight-medium">Subtotal</span>
                  </td>
                  <td>
                    {{ group.subTotal | currency }}
                  </td>
                </tr>
              </template>
              <tr class="font-weight-bold">
                <td colspan="3" class="text-right">
                  <span class="">Total</span>
                </td>
                <td>
                  {{ invoice.amount | currency }}
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>

        <div v-if="invoice?.status === 'PAID'" class="mt-4">
          <v-divider class="mb-3" />
          <p class="text-caption mb-1">
            <b>Valor recebido:</b> {{ invoice.paidAmount | currency }}
          </p>
          <p class="text-caption mb-0">
            <b>Data do recebimento:</b>
            {{ invoice.paidDate | date("DD/MM/YYYY") }}
          </p>
        </div>

        <div v-if="hasCommission" class="mt-4">
          <v-divider class="mb-3" />
          <p class="text-caption mb-1">
            <b>Comissionado:</b> {{ invoice.commissionUserName || "—" }}
          </p>
          <p class="text-caption mb-1">
            <b>Porcentagem:</b> {{ invoice.commissionPercentage }}%
          </p>
          <p class="text-caption mb-2">
            <b>Comissão:</b> {{ invoice.commissionAmount | currency }}
          </p>
          <v-chip
            v-if="invoice.CommissionEntry"
            small
            label
            :color="getCashEntryColor(invoice.CommissionEntry)"
            :to="getCashEntryLink(invoice.CommissionEntry)"
          >
            Comissão: {{ getCommissionLabel(invoice.CommissionEntry) }}
          </v-chip>
        </div>

        <div v-if="invoice?.CashEntry" class="mt-4">
          <v-divider class="mb-3" />
          <v-chip
            small
            label
            :color="getCashEntryColor(invoice.CashEntry)"
            :to="getCashEntryLink(invoice.CashEntry)"
          >
            Lançamento: {{ getCashEntryLabel(invoice.CashEntry) }}
          </v-chip>
        </div>
      </v-card-text>
      <v-card-actions>
        <p class="text-caption mb-0 mt-4">
          <b>Última atualização:</b>
          {{ invoice?.updatedAt | date("DD/MM/YYYY HH:mm") }}
        </p>
        <v-spacer />
        <v-btn text small @click="close">Fechar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import INVOICE_DEFINITIONS from "@/definitions/InvoiceDefinitions";
import CASH_DEFINITIONS from "@/definitions/CashDefinitions";

export default {
  name: "InvoiceDetails",
  data() {
    return {
      dialog: false,
      invoice: {},
      showFreeItems: false,
      INVOICE_DEFINITIONS,
    };
  },
  methods: {
    open(invoice) {
      this.invoice = invoice;
      this.dialog = true;
    },
    close() {
      this.dialog = false;
    },
    formatUsage(usage, itemKey) {
      const type = INVOICE_DEFINITIONS.itemKey[itemKey].type;
      return this.$options.filters[type](usage);
    },
    getCashEntryLabel(cashEntry) {
      return CASH_DEFINITIONS.status[cashEntry.status]?.label || cashEntry.status;
    },
    getCommissionLabel(cashEntry) {
      if (cashEntry.status === "SETTLED") {
        return CASH_DEFINITIONS.settledStatusLabel.EXPENSE;
      }
      return CASH_DEFINITIONS.status[cashEntry.status]?.label || cashEntry.status;
    },
    getCashEntryColor(cashEntry) {
      return CASH_DEFINITIONS.status[cashEntry.status]?.color || "grey";
    },
    getCashEntryLink(cashEntry) {
      return {
        name: "ultimate.cash-control",
        query: { entryId: cashEntry.id },
      };
    },
  },
  filters: {},
  mounted() {
    this.$parent.$on("invoice-details", this.open);
  },
  computed: {
    hasCommission() {
      return this.invoice?.commissionAmount != null;
    },
    itemsGrouped() {
      var items = this.invoice.Items;
      if (!this.showFreeItems) items = items.filter((item) => item.priceRuleId);

      items = items.sort((a, b) =>
        this.INVOICE_DEFINITIONS.itemKey[a.itemKey].group.localeCompare(
          this.INVOICE_DEFINITIONS.itemKey[b.itemKey].group
        )
      );

      let lastGroup;
      const resultWithGroups = items.reduce((acc, item) => {
        const group = this.INVOICE_DEFINITIONS.itemKey[item.itemKey].group;
        if (group !== lastGroup) {
          lastGroup = group;
          const groupDefinition =
            this.INVOICE_DEFINITIONS.itemGroups[group] || {};
          console.log(groupDefinition);
          acc.push({
            ...groupDefinition,
            items: [],
            subTotal: 0,
          });
        }
        acc[acc.length - 1].items.push(item);
        acc[acc.length - 1].subTotal += item.totalAmount;
        return acc;
      }, []);
      return resultWithGroups;
    },
  },
  beforeDestroy() {
    this.$parent.$off("invoice-details", this.open);
  },
};
</script>